import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { trackEvent } from 'dibs-tracking';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespPlaceholderTile } from '../SbRespSearchResultContainer/SbRespPlaceholderTile';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { useSbSelector } from '../../reducers/useSbSelector';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking';

import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { type SbSharedCreatorsResultContainer_viewer$key } from './__generated__/SbSharedCreatorsResultContainer_viewer.graphql';
import { type SbSharedCreatorsResultContainer_itemSearch$key } from './__generated__/SbSharedCreatorsResultContainer_itemSearch.graphql';
import { type SbSharedCreatorsResultContainer_items$key } from './__generated__/SbSharedCreatorsResultContainer_items.graphql';

import styles from './SbSharedCreatorsResultContainer.scss';

const ITEMS_PER_ROW = 4;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 3;

const viewerFragment = graphql`
    fragment SbSharedCreatorsResultContainer_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbSharedCreatorsResultContainer_itemSearch on ItemSearchQueryConnection {
        ...SharedItemTile_itemSearch
        ...useSbSharedItemTracking_itemSearch
        meta {
            header
        }
    }
`;
const itemsFragment = graphql`
    fragment SbSharedCreatorsResultContainer_items on ItemSearchResult @relay(plural: true) {
        item {
            serviceId
            ...FavoritesProvider_item
            ...useSbSharedItemTracking_item
            ...SharedItemTile_item
                @arguments(
                    isTrade: $isTrade
                    priceBookName: $priceBookName
                    showSeller: $showSeller
                )
        }
    }
`;

type Props = {
    viewer: SbSharedCreatorsResultContainer_viewer$key;
    itemSearch: SbSharedCreatorsResultContainer_itemSearch$key;
    items: SbSharedCreatorsResultContainer_items$key;
    vertical: string;
    link: string;
};

export const SbSharedCreatorsResultContainer: FC<Props> = ({
    viewer: viewerRef,
    items: itemsRef,
    itemSearch: itemSearchRef,
    vertical,
    link,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const itemNodes = useFragment(itemsFragment, itemsRef);

    const { isMobile } = useServerVarsContext();
    const afterDisplay = useSbSelector(state => state.relayVariables.variables.afterDisplay);
    const userId = useSbSelector(state => state.relayVariables.variables.userId);
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);

    const items = itemNodes.map(({ item }) => item).filter(filterFalsy) || [];
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items,
    });

    return (
        <>
            <div className={styles.container}>
                <FavoritesProvider
                    disable={!afterDisplay}
                    items={items}
                    location={locations.SEARCH_BROWSE}
                    userId={userId}
                    fetchFolder={!isMobile}
                >
                    {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                        <QuickViewProvider>
                            <>
                                {items.map((item, index) => {
                                    if (refetchInFlight && !isMobile) {
                                        return <SbRespPlaceholderTile key={index} />;
                                    }
                                    return (
                                        <SharedItemTile
                                            showCreatorName
                                            showSellerName={false}
                                            itemSearch={itemSearch}
                                            key={item?.serviceId || index}
                                            item={item}
                                            user={null}
                                            favorites={favorites}
                                            index={index}
                                            lazyLoadStartIndex={
                                                isMobile ? undefined : LAZY_LOAD_ITEM_TILE_INDEX
                                            }
                                            onContentVisible={() =>
                                                fireItemImpressionTracking({
                                                    itemId: item?.serviceId,
                                                    index,
                                                })
                                            }
                                            onClick={() =>
                                                fireItemClickTracking({
                                                    itemId: item?.serviceId,
                                                    index,
                                                })
                                            }
                                            imageLoadVerticalOffset={
                                                isMobile
                                                    ? VERTICAL_OFFSET_MOBILE_IMG
                                                    : VERTICAL_OFFSET_SB_RESP_IMG
                                            }
                                            customClassName={styles.tileWidth}
                                            viewer={viewer}
                                            mlFreightQuotesAbTestVariant={null}
                                        />
                                    );
                                })}
                            </>
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </div>
            <div className={styles.shopNowButton}>
                <div className={styles.shopNowDescription}>
                    <FormattedMessage
                        id="sb.SbSharedCreatorsResultContainer.shopNowDescription"
                        defaultMessage="Browse all {vertical} from {creator}"
                        values={{ vertical, creator: itemSearch?.meta?.header }}
                    />
                </div>
                <Button
                    type="secondary"
                    size="medium"
                    href={link}
                    dataTn="shop-now-button"
                    className={styles.shopNowButton}
                    fullWidth={isMobile ? true : false}
                    onClick={() =>
                        trackEvent({
                            category: 'promo interaction',
                            action: 'creator shop now click',
                            label: 'none',
                        })
                    }
                >
                    <FormattedMessage
                        id="sb.SbSharedCreatorsResultContainer.shopNowCTA"
                        defaultMessage="Shop Now"
                    />
                </Button>
            </div>
        </>
    );
};
